import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Button
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

/**
 * @component BlogPostPreview
 * @description Preview card for blog posts about ethical mineral trading
 *
 * SEO Considerations:
 * - Uses semantic HTML structure with proper heading hierarchy
 * - Includes keyword-rich content about ethical mineral trading
 * - Provides descriptive alt text for blog post images
 * - Implements proper linking structure for blog posts
 * - Enhances content with relevant tags for better categorization
 */
const BlogPostPreview = ({ post }) => {
  const {
    title,
    slug,
    excerpt,
    date,
    image,
    imageAlt,
    tags
  } = post;

  // State to track image loading errors
  const [imageError, setImageError] = useState(false);

  // Fallback image when the actual image fails to load
  const fallbackImage = '/images/laprecio-logo.png';

  // Handle image loading error
  const handleImageError = () => {
    setImageError(true);
  };

  /**
   * Renders the blog post tags with SEO-friendly keywords
   *
   * @returns {JSX.Element} Rendered tags
   */
  const renderTags = () => {
    if (!tags || tags.length === 0) return null;

    return (
      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {tags.map(tag => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            sx={{
              bgcolor: 'rgba(234, 179, 8, 0.1)',
              color: '#ca8a04',
              fontSize: '0.7rem'
            }}
          />
        ))}
      </Box>
    );
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        border: '2px solid #f5e5a5',
        borderLeft: '6px solid #eab308',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 12px 30px rgba(0,0,0,0.15)'
        }
      }}
    >
      <Box sx={{ position: 'relative', height: 200, overflow: 'hidden' }}>
        <CardMedia
          component="img"
          height="200"
          image={imageError ? fallbackImage : image}
          alt={imageAlt || `Blog post about ${title}`}
          onError={handleImageError}
          sx={{
            objectFit: imageError ? 'contain' : 'cover',
            bgcolor: imageError ? 'rgba(234, 179, 8, 0.1)' : 'inherit',
            p: imageError ? 2 : 0,
            height: '100%',
            width: '100%',
            position: 'absolute',
            top: 0,
            left: 0
          }}
        />
      </Box>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="caption" color="text.secondary" gutterBottom>
          {date}
        </Typography>

        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            fontSize: '1.25rem',
            lineHeight: 1.3
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          paragraph
          sx={{
            mb: 2,
            flexGrow: 1,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {excerpt}
        </Typography>

        {renderTags()}

        <Box sx={{ mt: 'auto', pt: 2 }}>
          <Button
            component={RouterLink}
            to={`/blog/${slug}`}
            variant="text"
            color="primary"
            sx={{
              fontWeight: 'bold',
              '&:hover': {
                bgcolor: 'rgba(234, 179, 8, 0.1)'
              }
            }}
          >
            Read More →
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default BlogPostPreview;
